import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle2,
  ArrowLeft,
  ExternalLink,
  Facebook,
  Image as ImageIcon,
  Instagram,
  Link as LinkIcon,
  MessageCircle,
  MoreHorizontal,
  Play,
  PanelRight,
  RefreshCw,
  Search,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { API } from '../App';
import { useWebSocketContext } from '../context/WebSocketContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { cn } from '../lib/utils';
import useMediaQuery from '../hooks/useMediaQuery';
import { readCommentsCache, writeCommentsCache } from '../utils/commentsCache';

const platformMeta = {
  instagram: { label: 'Instagram', Icon: Instagram, className: 'text-pink-400 bg-pink-500/10 border-pink-500/20' },
  facebook: { label: 'Facebook', Icon: Facebook, className: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
};

// Meta is currently rejecting Instagram comment private replies at the app
// capability layer. Keep the completed UI/API flow behind this switch until
// Meta resolves the entitlement, so agents are not offered a failing action.
const COMMENT_DIRECT_MESSAGES_ENABLED = false;

const formatTime = (value) => {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  const elapsed = Date.now() - date.getTime();
  if (elapsed < 60 * 60 * 1000) return `${Math.max(1, Math.floor(elapsed / 60000))}m`;
  if (elapsed < 24 * 60 * 60 * 1000) return `${Math.floor(elapsed / 3600000)}h`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatInboxTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const activityDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysAgo = Math.floor((today.getTime() - activityDate.getTime()) / 86400000);

  if (daysAgo <= 0) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (daysAgo < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const initials = (name) => (name || 'U').split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();

const requestErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const parts = [detail.message, detail.error].filter(Boolean);
    if (parts.length) return parts.join(': ');
  }
  return error?.message || fallback;
};

const Avatar = ({ name, src, className = '' }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <div className={cn('shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold', className)}>
      {src && !failed ? <img src={src} alt={name || 'User'} className="h-full w-full object-cover" onError={() => setFailed(true)} /> : initials(name)}
    </div>
  );
};

const PostThumbnail = ({ post, platform }) => {
  const [failed, setFailed] = useState(false);
  const mediaType = String(post?.media_type || '').toUpperCase();
  const isVideo = mediaType === 'VIDEO' || mediaType === 'REEL';
  const source = post?.thumbnail_url || (!isVideo ? post?.media_url : null);
  useEffect(() => setFailed(false), [source]);

  return (
    <div className={cn(
      'relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-inset',
      platform === 'facebook'
        ? 'bg-blue-500/10 ring-blue-500/25'
        : 'bg-gradient-to-br from-fuchsia-500/15 to-orange-400/10 ring-pink-500/25'
    )}>
      {source && !failed ? (
        <img src={source} alt="Post preview" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : isVideo ? (
        <Play className="h-5 w-5 text-[var(--tg-text-muted)]" fill="currentColor" />
      ) : (
        <ImageIcon className="h-5 w-5 text-[var(--tg-text-muted)]" />
      )}
    </div>
  );
};

const PlatformBadge = ({ platform }) => {
  const meta = platformMeta[platform] || platformMeta.instagram;
  const Icon = meta.Icon;
  return <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold', meta.className)}><Icon className="h-3 w-3" />{meta.label}</span>;
};

const QueueItem = ({ item, active, onClick }) => {
  const post = item.post || {};
  const latestComment = item.latestComment || item.comments?.[0] || {};
  const MetaIcon = (platformMeta[item.platform] || platformMeta.instagram).Icon;
  return (
    <button type="button" onClick={onClick} className={cn('group block w-full max-w-full overflow-hidden border-l-2 px-3 py-3 text-left transition-colors', active ? (item.platform === 'facebook' ? 'border-blue-500 bg-blue-500/10' : 'border-pink-500 bg-pink-500/10') : 'border-transparent hover:bg-[var(--tg-chat-hover)]')}>
      <div className="flex w-full min-w-0 max-w-full gap-3 overflow-hidden">
        <div className="relative shrink-0">
          <PostThumbnail post={post} platform={item.platform} />
          <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-[var(--tg-surface)] bg-[var(--tg-surface)]"><MetaIcon className={cn('h-3.5 w-3.5', item.platform === 'facebook' ? 'text-blue-500' : 'text-pink-500')} /></span>
        </div>
        <div className="w-0 min-w-0 flex-1 overflow-hidden">
          <div className="flex w-full min-w-0 items-center gap-2"><p className="block min-w-0 flex-1 truncate text-sm font-semibold text-[var(--tg-text-primary)]" title={post.caption || 'Post comments'}>{post.caption || 'Post comments'}</p><span className="ml-auto shrink-0 whitespace-nowrap text-[11px] text-[var(--tg-text-muted)]">{formatInboxTime(item.lastActivity)}</span></div>
          <p className="mt-0.5 block w-full truncate text-xs text-[var(--tg-text-secondary)]"><strong className="font-medium text-[var(--tg-text-primary)]">{latestComment.username || 'Someone'}</strong> commented · {item.commentCount} {item.commentCount === 1 ? 'comment' : 'comments'}</p>
          <p className="mt-1 block w-full truncate text-xs text-[var(--tg-text-muted)]">{latestComment.text || 'No comment text'}</p>
        </div>
      </div>
    </button>
  );
};

const InlineReplyComposer = ({ target, value, sending, onChange, onSend, onCancel }) => {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <form
      className="mt-3 rounded-lg border border-[var(--tg-accent-strong)]/35 bg-[var(--tg-surface-muted)] p-3 shadow-sm"
      onSubmit={(event) => { event.preventDefault(); onSend(); }}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="min-w-0 truncate text-xs text-[var(--tg-text-secondary)]">Replying to <span className="font-semibold text-[var(--tg-text-primary)]">{target.username || 'this comment'}</span></p>
        <button type="button" onClick={onCancel} className="shrink-0 text-xs text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]">Cancel</button>
      </div>
      <Textarea
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();
            onSend();
          }
        }}
        aria-label={`Reply to ${target.username || 'comment'}`}
        placeholder={`Reply to ${target.username || 'comment'}…`}
        className="min-h-[76px] resize-y border-[var(--tg-border-soft)] bg-[var(--tg-surface)] text-sm"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[11px] text-[var(--tg-text-muted)]">Ctrl/Cmd + Enter to send</span>
        <Button type="submit" size="sm" disabled={!value.trim() || sending}>
          {sending ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
          Reply
        </Button>
      </div>
    </form>
  );
};

const DirectMessageComposer = ({ target, platform, value, sending, onChange, onSend, onClose }) => {
  const inputRef = useRef(null);
  const meta = platformMeta[platform] || platformMeta.instagram;
  const Icon = meta.Icon;
  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <form className="fixed inset-x-3 bottom-3 z-50 flex min-h-[280px] flex-col rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-4 shadow-2xl sm:inset-x-auto sm:right-5 sm:w-[380px]" onSubmit={(event) => { event.preventDefault(); onSend(); }} aria-label={`Direct message to ${target.username || 'commenter'}`}>
      <div className="flex items-center gap-3 border-b border-[var(--tg-border-soft)] pb-3">
        <Avatar name={target.username} src={target.profile_pic} className="h-9 w-9" />
        <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-[var(--tg-text-primary)]">{target.username || 'Commenter'}</p><span className={cn('mt-0.5 inline-flex items-center gap-1 text-[11px]', platform === 'facebook' ? 'text-blue-400' : 'text-pink-400')}><Icon className="h-3 w-3" />Direct message</span></div>
        <button type="button" onClick={onClose} className="rounded p-1 text-[var(--tg-text-muted)] transition-colors hover:bg-[var(--tg-chat-hover)] hover:text-[var(--tg-text-primary)]" aria-label="Close direct message"><X className="h-4 w-4" /></button>
      </div>
      <Textarea ref={inputRef} value={value} onChange={(event) => onChange(event.target.value)} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') { event.preventDefault(); onSend(); } }} placeholder="Write a message…" className="mt-4 min-h-[130px] flex-1 resize-none border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] text-sm" />
      <div className="mt-3 flex items-center justify-end gap-2"><Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={sending}>Cancel</Button><Button type="submit" size="sm" disabled={!value.trim() || sending}>{sending ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}Send</Button></div>
    </form>
  );
};

const CommentMessage = ({ item, isRoot, onReply, onSendMessage, replying, reply, sending, onReplyChange, onSend, onCancel, onDelete }) => (
  <article className={cn('flex gap-3 px-5 py-4', !isRoot && 'ml-8 border-l border-[var(--tg-border-soft)]')}>
    <Avatar name={item.username} src={item.profile_pic} className="h-9 w-9" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2"><span className="text-sm font-semibold text-[var(--tg-text-primary)]">{item.username || 'Unknown user'}</span><span className="text-xs text-[var(--tg-text-muted)]">{formatTime(item.timestamp_ist)}</span></div>
      <p className="mt-1 text-sm leading-6 text-[var(--tg-text-primary)] whitespace-pre-wrap">{item.text || 'No comment text'}</p>
      <div className="mt-2 flex items-center gap-3 text-xs text-[var(--tg-text-muted)]">
        <button type="button" className="hover:text-[var(--tg-accent-strong)]" onClick={() => onReply(item)}>Reply</button>
        {COMMENT_DIRECT_MESSAGES_ENABLED && item.author_id !== item.page_id && <button type="button" className="hover:text-[var(--tg-accent-strong)]" onClick={() => onSendMessage(item)}>Send message</button>}
        {typeof item.like_count === 'number' && <span>{item.like_count} likes</span>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={`More actions for ${item.username || 'comment'}`} className="rounded p-0.5 hover:bg-[var(--tg-chat-hover)] hover:text-[var(--tg-text-primary)]"><MoreHorizontal className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="border-[var(--tg-border-soft)] bg-[var(--tg-surface)]">
            <DropdownMenuItem onSelect={() => onDelete(item)} className="cursor-pointer text-rose-400 focus:bg-rose-500/10 focus:text-rose-300">
              <Trash2 className="h-4 w-4" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {replying && <InlineReplyComposer target={item} value={reply} sending={sending} onChange={onReplyChange} onSend={onSend} onCancel={onCancel} />}
    </div>
  </article>
);

const SocialComments = ({ selectedPlatform = 'all', cacheScope = 'current-user' }) => {
  const isCompact = useMediaQuery('(max-width: 1023px)');
  const { lastMessage } = useWebSocketContext();
  const requestsInFlight = useRef(new Set());
  const [platform, setPlatform] = useState(selectedPlatform === 'facebook' ? 'facebook' : 'instagram');
  const [items, setItems] = useState({ instagram: [], facebook: [] });
  const [loaded, setLoaded] = useState({ instagram: false, facebook: false });
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [syncState, setSyncState] = useState({ instagram: 'idle', facebook: 'idle' });
  const [cacheInfo, setCacheInfo] = useState({ instagram: null, facebook: null });
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [replyTarget, setReplyTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dmTarget, setDmTarget] = useState(null);
  const [dmMessage, setDmMessage] = useState('');
  const [dmSending, setDmSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [visiblePostCount, setVisiblePostCount] = useState(20);
  const [compactPane, setCompactPane] = useState('queue');
  const [showCompactPost, setShowCompactPost] = useState(false);

  const normalize = useCallback((records, source) => {
    const threads = new Map();

    (records || []).forEach((record) => {
      const post = record.post ? {
        ...record.post,
        username: record.post.username || 'Your page',
        media_url: record.post.media_url || null,
        thumbnail_url: record.post.thumbnail_url || record.post.thumbnail || null,
        timestamp_ist: record.post.timestamp_ist || record.post.timestamp || null,
      } : null;
      const comment = {
        ...record,
        platform: source,
        username: record.username || 'Unknown user',
        text: record.text || '',
        timestamp_ist: record.timestamp_ist || record.timestamp || null,
        profile_pic: record.profile_pic || record.profile_pic_url || null,
        author_id: record.author_id || record.author?.id || null,
        page_id: record.page_id || post?.page_id || null,
        post,
        replies: (record.replies || []).map((child) => ({
          ...child,
          parent_comment_id: record.id,
          username: child.username || 'Your page',
          text: child.text || '',
          timestamp_ist: child.timestamp_ist || child.timestamp || record.timestamp_ist || record.timestamp || null,
          profile_pic: child.profile_pic || child.profile_pic_url || null,
          platform: source,
          author_id: child.author_id || child.author?.id || null,
          page_id: child.page_id || record.page_id || post?.page_id || null,
        })),
      };
      const postId = String(post?.id || record.media_id || record.post_id || `comment-${record.id}`);
      const existing = threads.get(postId) || {
        id: postId,
        platform: source,
        post,
        comments: [],
        latestComment: comment,
        lastActivity: comment.timestamp_ist || post?.timestamp_ist || null,
      };
      existing.comments.push(comment);
      const currentTime = new Date(existing.lastActivity || 0).getTime();
      const commentTime = new Date(comment.timestamp_ist || 0).getTime();
      if (commentTime >= currentTime) {
        existing.latestComment = comment;
        existing.lastActivity = comment.timestamp_ist || existing.lastActivity;
      }
      threads.set(postId, existing);
    });

    return Array.from(threads.values())
      .map((thread) => {
        thread.comments.sort((left, right) => new Date(right.timestamp_ist || 0) - new Date(left.timestamp_ist || 0));
        return {
          ...thread,
          commentCount: thread.comments.reduce((total, comment) => total + 1 + (comment.replies?.length || 0), 0),
        };
      })
      .sort((left, right) => new Date(right.lastActivity || 0) - new Date(left.lastActivity || 0));
  }, []);

  const load = useCallback(async (target = platform) => {
    if (requestsInFlight.current.has(target)) return;
    requestsInFlight.current.add(target);
    setError('');
    let cachedRecord = null;

    try {
      cachedRecord = await readCommentsCache(cacheScope, target);
    } catch (cacheError) {
      console.warn('Could not restore cached comments:', cacheError);
    }

    if (cachedRecord) {
      const cachedThreads = cachedRecord.threads;
      setItems((previous) => ({ ...previous, [target]: cachedThreads }));
      setLoaded((previous) => ({ ...previous, [target]: true }));
      setCacheInfo((previous) => ({ ...previous, [target]: cachedRecord.savedAt }));
      setSelectedId((current) => cachedThreads.some((item) => item.id === current) ? current : (cachedThreads[0]?.id || null));
      setSyncState((previous) => ({ ...previous, [target]: 'refreshing' }));
    } else {
      setSyncState((previous) => ({ ...previous, [target]: 'loading' }));
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await axios.get(`${API}/${target}/comments`, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      const next = normalize(response.data, target);
      const savedAt = Date.now();
      setItems((previous) => ({ ...previous, [target]: next }));
      setLoaded((previous) => ({ ...previous, [target]: true }));
      setCacheInfo((previous) => ({ ...previous, [target]: savedAt }));
      setSelectedId((current) => next.some((item) => item.id === current) ? current : (next[0]?.id || null));
      void writeCommentsCache(cacheScope, target, next);
    } catch (requestError) {
      const detail = requestErrorMessage(requestError, 'Could not load comments.');
      setError(cachedRecord ? `Showing saved data. Refresh failed: ${detail}` : detail);
    } finally {
      setSyncState((previous) => ({ ...previous, [target]: 'idle' }));
      requestsInFlight.current.delete(target);
    }
  }, [cacheScope, normalize, platform]);

  useEffect(() => { load(platform); }, [platform, load]);
  useEffect(() => { if (selectedPlatform !== 'all') setPlatform(selectedPlatform); }, [selectedPlatform]);
  useEffect(() => {
    if (!lastMessage) return;
    const isRelevant =
      (platform === 'instagram' && lastMessage.type === 'ig_comment')
      || (platform === 'facebook' && lastMessage.type === 'facebook_comment');
    if (isRelevant) load(platform);
  }, [lastMessage, load, platform]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = items[platform] || [];
    if (!query) return list;
    return list.filter((item) => [
      item.post?.caption,
      ...item.comments.flatMap((comment) => [
        comment.username,
        comment.text,
        ...(comment.replies || []).flatMap((replyItem) => [replyItem.username, replyItem.text]),
      ]),
    ].some((value) => value?.toLowerCase().includes(query)));
  }, [items, platform, search]);
  const selected = useMemo(() => (items[platform] || []).find((item) => item.id === selectedId) || null, [items, platform, selectedId]);
  const post = selected?.post || null;
  const comments = selected?.comments || [];
  const totalComments = selected?.commentCount || 0;
  const displayedItems = visibleItems.slice(0, visiblePostCount);
  const hasMorePosts = visibleItems.length > displayedItems.length;
  const remainingPostCount = Math.max(0, visibleItems.length - displayedItems.length);

  useEffect(() => { setVisiblePostCount(20); }, [platform, search]);

  const choose = (item) => {
    setSelectedId(item.id);
    setReply('');
    setReplyTarget(null);
    setDmTarget(null);
    setDmMessage('');
    if (isCompact) setCompactPane('thread');
  };

  const startReply = useCallback((target) => {
    setReplyTarget(target);
    setReply('');
  }, []);

  const cancelReply = useCallback(() => {
    setReply('');
    setReplyTarget(null);
  }, []);

  const closeDirectMessage = useCallback(() => {
    if (dmSending) return;
    setDmTarget(null);
    setDmMessage('');
  }, [dmSending]);

  const startDirectMessage = useCallback((target) => {
    if (!target?.author_id) {
      setNotice('Meta did not provide a messageable recipient for this comment yet. A direct message cannot be sent safely until that identity is available.');
      return;
    }
    setNotice('');
    setDmTarget(target);
    setDmMessage('');
  }, []);

  const sendDirectMessage = async () => {
    const target = dmTarget;
    if (!target || !dmMessage.trim() || dmSending) return;
    setDmSending(true);
    setError('');
    setNotice('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await axios.post(
        `${API}/${platform}/comments/${encodeURIComponent(target.id)}/direct-message`,
        {
          text: dmMessage.trim(),
          page_id: target.page_id || null,
          display_name: target.username || null,
          profile_pic_url: target.profile_pic || null,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      setNotice(response.data?.assigned_to_current_user
        ? 'Direct message sent. The unassigned chat is now assigned to you.'
        : 'Direct message sent. The existing chat assignment was kept.');
      setDmTarget(null);
      setDmMessage('');
    } catch (requestError) {
      setError(requestErrorMessage(requestError, 'Unable to send direct message.'));
    } finally {
      setDmSending(false);
    }
  };

  const sendReply = async () => {
    const targetComment = replyTarget;
    if (!selected || !targetComment || !reply.trim() || sending) return;
    setSending(true); setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      // Meta comment conversations are one reply level deep. Replying to an
      // existing reply should target its root comment while the inline UI
      // keeps the person's actual message in context.
      const apiTargetId = targetComment.parent_comment_id || targetComment.id;
      const replyText = reply.trim();
      const response = await axios.post(`${API}/${platform}/comments/${apiTargetId}/reply`, { text: replyText, page_id: targetComment.page_id || null }, { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 });
      const timestamp = response.data?.timestamp || new Date().toISOString();
      const optimisticReply = {
        id: response.data?.id || `local-reply-${Date.now()}`,
        parent_comment_id: apiTargetId,
        username: post?.username || 'Your page',
        text: replyText,
        timestamp_ist: timestamp,
        profile_pic: post?.profile_pic || post?.profile_pic_url || null,
        platform,
        author_id: targetComment.page_id || null,
        page_id: targetComment.page_id || null,
        isOptimistic: true,
      };
      setItems((previous) => ({
        ...previous,
        [platform]: (previous[platform] || []).map((thread) => thread.id !== selected.id ? thread : {
          ...thread,
          lastActivity: timestamp,
          commentCount: (thread.commentCount || 0) + 1,
          comments: (thread.comments || []).map((comment) => comment.id !== apiTargetId ? comment : {
            ...comment,
            replies: [...(comment.replies || []), optimisticReply],
          }),
        }),
      }));
      cancelReply();
      setNotice('Reply sent. It is shown immediately and will be confirmed during the next refresh.');
    } catch (requestError) {
      setError(requestErrorMessage(requestError, 'Unable to send reply.'));
    } finally { setSending(false); }
  };

  const deleteComment = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const headers = { Authorization: `Bearer ${token}` };
      if (platform === 'instagram') {
        await axios.delete(`${API}/comments/delete`, {
          headers,
          // The feed includes the Instagram business-account ID. Passing it
          // back avoids selecting a different account in a multi-Page setup.
          params: { comment_id: deleteTarget.id, page_id: deleteTarget.page_id || undefined },
          timeout: 30000,
        });
      } else {
        await axios.delete(`${API}/facebook/comments/${encodeURIComponent(deleteTarget.id)}`, { headers, timeout: 30000 });
      }

      // Meta has confirmed the deletion, so remove it locally straight away.
      // Waiting for the next full comments sync makes a successful action look
      // like it failed, especially while Instagram is slow to refresh.
      const deletedId = deleteTarget.id;
      const removeDeletedComment = (threads) => (threads || []).map((thread) => {
        const remainingComments = (thread.comments || [])
          .filter((comment) => comment.id !== deletedId)
          .map((comment) => ({
            ...comment,
            replies: (comment.replies || []).filter((replyItem) => replyItem.id !== deletedId),
          }));
        const allRemainingMessages = remainingComments.flatMap((comment) => [comment, ...(comment.replies || [])]);
        const latestMessage = allRemainingMessages.reduce((latest, item) => {
          if (!latest) return item;
          return new Date(item.timestamp_ist || 0).getTime() > new Date(latest.timestamp_ist || 0).getTime()
            ? item
            : latest;
        }, null);
        return {
          ...thread,
          comments: remainingComments,
          commentCount: allRemainingMessages.length,
          latestComment: latestMessage || {},
          lastActivity: latestMessage?.timestamp_ist || thread.post?.timestamp_ist || thread.lastActivity,
        };
      });
      const cachedThreads = removeDeletedComment(items[platform]);
      setItems((previous) => ({
        ...previous,
        [platform]: removeDeletedComment(previous[platform]),
      }));
      void writeCommentsCache(cacheScope, platform, cachedThreads).catch(() => {});
      if (replyTarget?.id === deleteTarget.id) cancelReply();
      setDeleteTarget(null);
      setNotice('Comment deleted. The post will be reconciled during the next refresh.');
    } catch (requestError) {
      setError(requestErrorMessage(requestError, 'Unable to delete this comment.'));
    } finally { setDeleting(false); }
  };

  const playablePost = ['VIDEO', 'REEL'].includes(String(post?.media_type || '').toUpperCase());
  const loading = syncState[platform] === 'loading';
  const refreshing = syncState[platform] === 'refreshing';
  const syncActive = loading || refreshing;
  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-2 sm:p-3 md:p-4">
      <div className="flex shrink-0 items-end justify-between gap-4 px-1">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--tg-text-primary)]">Comments &amp; Reviews</h1>
          <p className="mt-1 text-sm text-[var(--tg-text-muted)]">Review post conversations and reply without leaving TickleGram.</p>
        </div>
        <Button variant="outline" onClick={() => load(platform)} disabled={syncActive} className="shrink-0 border-[var(--tg-border-soft)] bg-[var(--tg-surface)]">
          <RefreshCw className={cn('mr-2 h-4 w-4', syncActive && 'animate-spin')} />{refreshing ? 'Updating' : 'Refresh'}
        </Button>
      </div>
      <div className="grid min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] shadow-card lg:grid-cols-[320px_minmax(0,1fr)_300px] xl:grid-cols-[380px_minmax(0,1fr)_300px]">
        <aside className={cn('min-h-0 min-w-0 flex-col border-r border-[var(--tg-border-soft)] bg-[var(--tg-surface)]', isCompact && compactPane !== 'queue' ? 'hidden' : 'flex')}>
          <div className="border-b border-[var(--tg-border-soft)] p-3">
            <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--tg-text-muted)]" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search posts and comments" className="h-9 pl-9 bg-[var(--tg-surface-muted)] border-[var(--tg-border-soft)]" /></div>
            <div className="mt-3 grid grid-cols-2 gap-1 rounded-lg bg-[var(--tg-surface-muted)] p-1"><button type="button" aria-pressed={platform === 'instagram'} onClick={() => setPlatform('instagram')} className={cn('flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors', platform === 'instagram' ? 'bg-[var(--tg-surface)] text-pink-400 shadow-sm' : 'text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]')}><Instagram className="h-3.5 w-3.5" />Instagram <span className="opacity-70">{loaded.instagram ? items.instagram.length : '—'}</span></button><button type="button" aria-pressed={platform === 'facebook'} onClick={() => setPlatform('facebook')} className={cn('flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors', platform === 'facebook' ? 'bg-[var(--tg-surface)] text-blue-400 shadow-sm' : 'text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]')}><Facebook className="h-3.5 w-3.5" />Facebook <span className="opacity-70">{loaded.facebook ? items.facebook.length : '—'}</span></button></div>
            {(refreshing || cacheInfo[platform]) && <p className="mt-2 text-[11px] text-[var(--tg-text-muted)]">{refreshing ? 'Showing saved data while checking Meta for updates…' : `Updated ${formatTime(cacheInfo[platform])}`}</p>}
            {error && <div className="mt-3 rounded-md border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs text-rose-400">{typeof error === 'string' ? error : 'Could not load comments.'}</div>}
            {notice && <div className="mt-3 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">{notice}</div>}
          </div>
          <ScrollArea className="flex-1"><div className="w-full min-w-0 max-w-full overflow-hidden">{loading && !(items[platform] || []).length ? <div className="p-4 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto" /><p className="text-gray-500 text-sm mt-2">Loading posts and comments...</p></div> : visibleItems.length ? <>{displayedItems.map((item) => <QueueItem key={item.id} item={item} active={item.id === selectedId} onClick={() => choose(item)} />)}<div className="border-t border-[var(--tg-border-soft)] p-3 text-center">{hasMorePosts ? <><Button variant="outline" size="sm" onClick={() => setVisiblePostCount((count) => count + 20)} className="border-[var(--tg-border-soft)]">Load 20 more posts</Button><p className="mt-2 text-[11px] text-[var(--tg-text-muted)]">{remainingPostCount} more post{remainingPostCount === 1 ? '' : 's'} available</p></> : <p className="text-xs text-[var(--tg-text-muted)]">Showing all {visibleItems.length} posts from the latest sync.</p>}</div></> : <div className="p-6 text-center text-sm text-[var(--tg-text-muted)]">No posts with comments found.</div>}</div></ScrollArea>
        </aside>

        <main className={cn('min-h-0 min-w-0 flex-col border-r border-[var(--tg-border-soft)]', isCompact && compactPane !== 'thread' ? 'hidden' : 'flex')}>
          {selected ? <>
            <header className="flex items-start gap-2 border-b border-[var(--tg-border-soft)] px-3 py-3 sm:gap-3 sm:px-5 sm:py-4">{isCompact && <Button variant="ghost" size="icon" onClick={() => setCompactPane('queue')} aria-label="Back to posts"><ArrowLeft className="h-4 w-4" /></Button>}<Avatar name={post?.username || 'Page'} src={post?.profile_pic || post?.profile_pic_url} className="h-10 w-10" /><div className="min-w-0 flex-1 overflow-hidden"><div className="flex min-w-0 items-center gap-2"><h1 className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold text-[var(--tg-text-primary)]">{post?.caption || 'Post comments'}</h1><PlatformBadge platform={platform} /></div><p className="mt-1 text-xs text-[var(--tg-text-muted)]">{totalComments} {totalComments === 1 ? 'comment' : 'comments'} · Last activity {formatTime(selected.lastActivity)}</p></div>{isCompact && <Button variant="outline" size="icon" onClick={() => setShowCompactPost((shown) => !shown)} aria-label="Toggle post preview"><PanelRight className="h-4 w-4" /></Button>}<Button variant="outline" size="icon" onClick={() => load(platform)} disabled={syncActive} className="border-[var(--tg-border-soft)]"><RefreshCw className={cn('h-4 w-4', syncActive && 'animate-spin')} /></Button></header>
            {isCompact && showCompactPost && <div className="max-h-[45vh] overflow-y-auto border-b border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] p-3"><div className="mx-auto max-w-sm"><div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-[var(--tg-surface)]">{post?.media_url ? playablePost ? <video src={post.media_url} poster={post.thumbnail_url || undefined} controls playsInline preload="metadata" className="h-full w-full object-contain" aria-label="Post video" /> : <img src={post.media_url} alt="Post" className="h-full w-full object-contain" /> : post?.thumbnail_url ? <img src={post.thumbnail_url} alt="Post" className="h-full w-full object-contain" /> : <ImageIcon className="m-auto h-full w-10 text-[var(--tg-text-muted)]" />}</div><p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[var(--tg-text-secondary)]">{post?.caption || 'This comment does not include post details.'}</p></div></div>}
            <ScrollArea className="flex-1"><div className="divide-y divide-[var(--tg-border-soft)]">{comments.map((comment) => <div key={comment.id}><CommentMessage item={comment} isRoot onReply={startReply} onSendMessage={startDirectMessage} replying={replyTarget?.id === comment.id} reply={reply} sending={sending} onReplyChange={setReply} onSend={sendReply} onCancel={cancelReply} onDelete={setDeleteTarget} />{(comment.replies || []).map((replyItem) => <CommentMessage key={replyItem.id} item={replyItem} onReply={startReply} onSendMessage={startDirectMessage} replying={replyTarget?.id === replyItem.id} reply={reply} sending={sending} onReplyChange={setReply} onSend={sendReply} onCancel={cancelReply} onDelete={setDeleteTarget} />)}</div>)}</div></ScrollArea>
            <footer className="border-t border-[var(--tg-border-soft)] px-4 py-3 text-xs text-[var(--tg-text-muted)]">Use <span className="font-medium text-[var(--tg-text-secondary)]">Reply</span> below a comment or reply to respond in context.</footer>
          </> : <div className="m-auto text-center text-sm text-[var(--tg-text-muted)]"><MessageCircle className="mx-auto mb-3 h-8 w-8 opacity-50" />Select a post to view its comments.</div>}
        </main>

        <aside className="hidden min-h-0 flex-col bg-[var(--tg-surface)] lg:flex">{selected ? <ScrollArea className="flex-1"><div className="p-4"><div className="relative overflow-hidden rounded-lg bg-[var(--tg-surface-muted)] aspect-[4/5]">{post?.media_url ? playablePost ? <video src={post.media_url} poster={post.thumbnail_url || undefined} controls playsInline preload="metadata" className="h-full w-full object-cover" aria-label="Post video" /> : <img src={post.media_url} alt="Post" className="h-full w-full object-cover" /> : post?.thumbnail_url ? <img src={post.thumbnail_url} alt="Post" className="h-full w-full object-cover" /> : <ImageIcon className="m-auto h-full w-10 text-[var(--tg-text-muted)]" />}{playablePost && !post?.media_url && <Play className="absolute inset-0 m-auto h-10 w-10 text-white" />}</div><div className="mt-4"><div className="flex items-center justify-between"><PlatformBadge platform={platform} />{post?.permalink && <a href={post.permalink} target="_blank" rel="noreferrer" className="text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]"><ExternalLink className="h-4 w-4" /></a>}</div><h2 className="mt-3 text-sm font-semibold text-[var(--tg-text-primary)]">{post?.username || 'Your page'}</h2><p className="mt-2 text-sm leading-6 text-[var(--tg-text-secondary)] whitespace-pre-wrap">{post?.caption || 'This post does not include a caption.'}</p></div><div className="mt-5 space-y-3 border-t border-[var(--tg-border-soft)] pt-4 text-xs"><p className="flex items-center gap-2 text-[var(--tg-text-secondary)]"><MessageCircle className="h-4 w-4" />{totalComments} {totalComments === 1 ? 'comment' : 'comments'} on this post</p><p className="flex items-center gap-2 text-[var(--tg-text-secondary)]"><CheckCircle2 className="h-4 w-4 text-emerald-400" />Ready for moderation</p>{post?.permalink && <a href={post.permalink} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[var(--tg-accent-strong)] hover:underline"><LinkIcon className="h-4 w-4" />Open original post</a>}</div></div></ScrollArea> : <div className="m-auto px-6 text-center text-sm text-[var(--tg-text-muted)]">Post details will appear here.</div>}</aside>
      </div>
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <AlertDialogContent className="border-[var(--tg-border-soft)] bg-[var(--tg-surface)] text-[var(--tg-text-primary)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--tg-text-secondary)]">This will remove {deleteTarget?.username ? `${deleteTarget.username}'s ` : 'the '}comment or reply from {platformMeta[platform]?.label || 'the platform'}. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)]">Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={deleteComment} disabled={deleting}>{deleting ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}{deleting ? 'Deleting' : 'Delete'}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {COMMENT_DIRECT_MESSAGES_ENABLED && dmTarget && <DirectMessageComposer target={dmTarget} platform={platform} value={dmMessage} sending={dmSending} onChange={setDmMessage} onSend={sendDirectMessage} onClose={closeDirectMessage} />}
    </section>
  );
};

export default SocialComments;
